import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    useNotify,
    useRedirect,
    Title,
} from 'react-admin';
import { useParams } from 'react-router-dom';
import { Card, CardContent, Button, TextField, Typography, Box, Tabs, Tab, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { html_beautify } from 'js-beautify';

const TextbookEditor = () => {
    const { id } = useParams();
    const notify = useNotify();
    const redirect = useRedirect();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [record, setRecord] = useState(null);
    const [tabValue, setTabValue] = useState(0);

    // Form state
    const [promptSnapshot, setPromptSnapshot] = useState('');
    const [material, setMaterial] = useState('');
    const [materialHtml, setMaterialHtml] = useState('');
    const [designBrief, setDesignBrief] = useState('');

    // WYSIWYG split-pane state
    const [htmlViewMode, setHtmlViewMode] = useState('split'); // 'edit' | 'split' | 'preview'
    const iframeRef = useRef(null);
    const previewTimerRef = useRef(null);
    const textareaRef = useRef(null);

    const scrollToSource = useCallback((clickedText) => {
        if (!textareaRef.current || !clickedText) return;
        const ta = textareaRef.current;
        const html = ta.value;

        // Try progressively shorter slices until we find a match
        let pos = -1;
        for (let len = Math.min(clickedText.length, 60); len >= 8; len = Math.floor(len * 0.7)) {
            pos = html.indexOf(clickedText.substring(0, len));
            if (pos !== -1) break;
        }
        if (pos === -1) return;

        // Scroll textarea so the match is near the top
        const lineHeight = 18; // fontSize 12 * lineHeight 1.5
        const linesBefore = html.substring(0, pos).split('\n').length - 1;
        ta.scrollTop = Math.max(0, linesBefore * lineHeight - 60);

        // Highlight the matched text
        ta.focus();
        ta.setSelectionRange(pos, pos + Math.min(clickedText.length, 60));
    }, []);

    const writeToIframe = useCallback((htmlContent) => {
        if (!iframeRef.current) return;
        const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
        doc.open();
        doc.write(htmlContent || '<p style="color:#aaa;padding:24px;font-family:sans-serif">No content yet.</p>');
        doc.close();
        doc.addEventListener('click', (e) => {
            const text = e.target.textContent?.trim();
            if (text && text.length >= 4) scrollToSource(text);
        });
    }, [scrollToSource]);

    const updatePreview = useCallback((html) => {
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = setTimeout(async () => {
            if (!iframeRef.current) return;
            // Sign any media/ paths so images render in the preview
            if (html && html.includes('src="media/')) {
                try {
                    const backendBase = process.env.REACT_APP_BACKEND_BASE ?? 'http://localhost:8000';
                    const res = await fetch(`${backendBase}/api/admin/sign_html`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ html }),
                    });
                    if (res.ok) {
                        const data = await res.json();
                        writeToIframe(data.html);
                        return;
                    }
                } catch (_) { /* fall through to unsighed render */ }
            }
            writeToIframe(html);
        }, 400);
    }, [scrollToSource, writeToIframe]);

    useEffect(() => {
        if (tabValue === 2 && htmlViewMode !== 'edit') {
            updatePreview(materialHtml);
        }
    }, [materialHtml, tabValue, htmlViewMode, updatePreview]);

    // Trigger preview when switching to preview-visible modes
    useEffect(() => {
        if (tabValue === 2 && htmlViewMode !== 'edit') {
            // Small delay so iframe is mounted
            setTimeout(() => updatePreview(materialHtml), 50);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [htmlViewMode, tabValue]);

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        try {
            const backendBase = process.env.REACT_APP_BACKEND_BASE ?? 'http://localhost:8000';
            const response = await fetch(`${backendBase}/api/course_wizard/${id}`, {
                headers: {
                    'X-User-Id': localStorage.getItem('userId') || '91a37b17-10ce-41c2-860d-890605802763'
                }
            });

            if (!response.ok) throw new Error('Failed to fetch data');

            const data = await response.json();
            setRecord(data);

            // Try to pretty-print prompt_snapshot if it's valid JSON
            let formattedPrompt = data.prompt_snapshot || '';
            try {
                // Find the last closing brace to ignore trailing content like newlines
                let cleanPrompt = formattedPrompt.trim();
                const lastBraceIndex = cleanPrompt.lastIndexOf('}');
                if (lastBraceIndex !== -1 && lastBraceIndex < cleanPrompt.length - 1) {
                    cleanPrompt = cleanPrompt.substring(0, lastBraceIndex + 1);
                }

                const parsedPrompt = JSON.parse(cleanPrompt);
                formattedPrompt = JSON.stringify(parsedPrompt, null, 2);
            } catch (e) {
                // Not JSON or parse error, leave as is
                console.warn("Could not auto-format prompt snapshot:", e);
            }
            setPromptSnapshot(formattedPrompt);

            setMaterial(JSON.stringify(data.material || {}, null, 2));
            setMaterialHtml(data.material_html || '');
            setDesignBrief(JSON.stringify(data.design_brief || {}, null, 2));
            setLoading(false);
        } catch (error) {
            console.error(error);
            notify('Error loading textbook data', { type: 'error' });
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Validate JSON
            let parsedMaterial, parsedDesignBrief;
            try {
                parsedMaterial = JSON.parse(material);
            } catch (e) {
                notify('Invalid JSON in Material field', { type: 'error' });
                setSaving(false);
                return;
            }
            try {
                parsedDesignBrief = JSON.parse(designBrief);
            } catch (e) {
                notify('Invalid JSON in Design Brief field', { type: 'error' });
                setSaving(false);
                return;
            }

            const payload = {
                prompt_snapshot: promptSnapshot,
                material: parsedMaterial,
                material_html: materialHtml,
                design_brief: parsedDesignBrief
            };

            const backendBase = process.env.REACT_APP_BACKEND_BASE ?? 'http://localhost:8000';
            const response = await fetch(`${backendBase}/api/admin/course_material_content/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-User-Id': localStorage.getItem('userId') || '91a37b17-10ce-41c2-860d-890605802763'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error('Failed to save');

            notify('Textbook updated successfully', { type: 'success' });
        } catch (error) {
            console.error(error);
            notify('Error saving textbook', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            notify('Copied to clipboard', { type: 'success' });
        } catch (err) {
            console.error('Failed to copy:', err);
            notify('Failed to copy', { type: 'warning' });
        }
    };

    if (loading) return <Typography>Loading...</Typography>;

    return (
        <Card sx={{ m: 2 }}>
            <Title title={`Edit Textbook: ${record?.course_id || 'Unknown'}`} />
            <CardContent>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => redirect('/course_material_content')}
                    >
                        Back to List
                    </Button>
                    <Typography variant="h5">Textbook Editor</Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        Save Changes
                    </Button>
                </div>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab label="Prompt Snapshot" />
                        <Tab label="Material (JSON)" />
                        <Tab label="Material HTML" />
                        <Tab label="Design Brief" />
                    </Tabs>
                </Box>

                <div role="tabpanel" hidden={tabValue !== 0}>
                    {tabValue === 0 && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                <Button startIcon={<ContentCopyIcon />} size="small" onClick={() => handleCopy(promptSnapshot)}>
                                    Copy
                                </Button>
                            </Box>
                            <TextField
                                fullWidth
                                multiline
                                minRows={20}
                                value={promptSnapshot}
                                onChange={(e) => setPromptSnapshot(e.target.value)}
                                variant="outlined"
                                label="Prompt Snapshot"
                                InputProps={{ style: { fontFamily: 'monospace' } }}
                            />
                        </Box>
                    )}
                </div>

                <div role="tabpanel" hidden={tabValue !== 1}>
                    {tabValue === 1 && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                <Button startIcon={<ContentCopyIcon />} size="small" onClick={() => handleCopy(material)}>
                                    Copy
                                </Button>
                            </Box>
                            <TextField
                                fullWidth
                                multiline
                                minRows={20}
                                value={material}
                                onChange={(e) => setMaterial(e.target.value)}
                                variant="outlined"
                                label="Material (JSON)"
                                InputProps={{ style: { fontFamily: 'monospace' } }}
                            />
                        </Box>
                    )}
                </div>

                <div role="tabpanel" hidden={tabValue !== 2}>
                    {tabValue === 2 && (
                        <Box>
                            {/* Toolbar */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <ToggleButtonGroup
                                    value={htmlViewMode}
                                    exclusive
                                    onChange={(_, val) => { if (val) setHtmlViewMode(val); }}
                                    size="small"
                                >
                                    <ToggleButton value="edit">
                                        <Tooltip title="Source editor only">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <CodeIcon fontSize="small" />
                                                <span style={{ fontSize: 12 }}>HTML</span>
                                            </Box>
                                        </Tooltip>
                                    </ToggleButton>
                                    <ToggleButton value="split">
                                        <Tooltip title="Side-by-side editor and preview">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <ViewSidebarIcon fontSize="small" />
                                                <span style={{ fontSize: 12 }}>Split</span>
                                            </Box>
                                        </Tooltip>
                                    </ToggleButton>
                                    <ToggleButton value="preview">
                                        <Tooltip title="Preview only">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <VisibilityIcon fontSize="small" />
                                                <span style={{ fontSize: 12 }}>Preview</span>
                                            </Box>
                                        </Tooltip>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Tooltip title="Indent and format the HTML source">
                                        <Button
                                            startIcon={<AutoFixHighIcon />}
                                            size="small"
                                            onClick={() => {
                                                // Protect mermaid blocks from being reformatted
                                                const saved = [];
                                                const guarded = materialHtml.replace(
                                                    /(<div[^>]*class="[^"]*mermaid[^"]*"[^>]*>)([\s\S]*?)(<\/div>)/g,
                                                    (_, open, content, close) => {
                                                        saved.push(content);
                                                        return `${open}%%MERMAID_${saved.length - 1}%%${close}`;
                                                    }
                                                );
                                                const formatted = html_beautify(guarded, {
                                                    indent_size: 2,
                                                    wrap_line_length: 120,
                                                    preserve_newlines: false,
                                                    max_preserve_newlines: 1,
                                                    end_with_newline: true,
                                                });
                                                const restored = formatted.replace(
                                                    /%%MERMAID_(\d+)%%/g,
                                                    (_, i) => saved[parseInt(i)]
                                                );
                                                setMaterialHtml(restored);
                                            }}
                                        >
                                            Format
                                        </Button>
                                    </Tooltip>
                                    <Button startIcon={<ContentCopyIcon />} size="small" onClick={() => handleCopy(materialHtml)}>
                                        Copy HTML
                                    </Button>
                                </Box>
                            </Box>

                            {/* Editor area */}
                            <Box sx={{ display: 'flex', gap: 1, height: 680 }}>

                                {/* Source pane */}
                                {htmlViewMode !== 'preview' && (
                                    <Box sx={{ flex: htmlViewMode === 'split' ? '0 0 50%' : '1 1 100%', display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                            HTML Source
                                        </Typography>
                                        <textarea
                                            ref={textareaRef}
                                            value={materialHtml}
                                            onChange={(e) => setMaterialHtml(e.target.value)}
                                            spellCheck={false}
                                            style={{
                                                flex: 1,
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                fontFamily: 'monospace',
                                                fontSize: 12,
                                                lineHeight: 1.5,
                                                padding: '12px',
                                                border: '1px solid #ccc',
                                                borderRadius: 4,
                                                resize: 'none',
                                                outline: 'none',
                                                backgroundColor: '#1e1e1e',
                                                color: '#d4d4d4',
                                            }}
                                        />
                                    </Box>
                                )}

                                {/* Preview pane */}
                                {htmlViewMode !== 'edit' && (
                                    <Box sx={{ flex: htmlViewMode === 'split' ? '0 0 50%' : '1 1 100%', display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                            Live Preview — <em>click any text to jump to it in the source</em>
                                        </Typography>
                                        <iframe
                                            ref={iframeRef}
                                            title="HTML Preview"
                                            style={{
                                                flex: 1,
                                                width: '100%',
                                                border: '1px solid #ccc',
                                                borderRadius: 4,
                                                backgroundColor: '#fff',
                                            }}
                                            sandbox="allow-scripts allow-same-origin"
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </div>

                <div role="tabpanel" hidden={tabValue !== 3}>
                    {tabValue === 3 && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                <Button startIcon={<ContentCopyIcon />} size="small" onClick={() => handleCopy(designBrief)}>
                                    Copy
                                </Button>
                            </Box>
                            <TextField
                                fullWidth
                                multiline
                                minRows={20}
                                value={designBrief}
                                onChange={(e) => setDesignBrief(e.target.value)}
                                variant="outlined"
                                label="Design Brief (JSON)"
                                InputProps={{ style: { fontFamily: 'monospace' } }}
                            />
                        </Box>
                    )}
                </div>

            </CardContent>
        </Card>
    );
};

export default TextbookEditor;
